import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import type { AssistanceRequestBody, OnboardingPlayKey, OnboardingStatus } from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';

const api = getApiClient();

/**
 * Fetch the state-driven onboarding play menu for the selected (real) campaign.
 */
export function useOnboardingMenu() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.onboarding.menu(),
    queryFn: () => api.onboarding.getMenu(),
    enabled: !!selectedCampaign,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Read the current onboarding progress for this user + campaign. Used to avoid regressing a
 * returning manager's status when they revisit the checklist (e.g. from a help link).
 */
export function useOnboardingStatus() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.onboarding.status(),
    queryFn: () => api.onboarding.getStatus(),
    enabled: !!selectedCampaign,
  });
}

/**
 * Scaffold a chosen play — generates the real artifact (e.g. a canvassing script) in the
 * manager's campaign. Returns the generated artifact.
 */
export function useScaffoldPlay() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (play: OnboardingPlayKey) => api.onboarding.scaffold({ play }),
    onSuccess: () => {
      // The script play creates CampaignQuestions — refresh anything that reads them, including the
      // questions list the onboarding revisit reads to show the "script ready" card vs the hero.
      // Invalidate the campaign-scoped onboarding keys, not the unscoped `all` prefix, so a campaign
      // switch racing the scaffold can't wipe another campaign's cached menu/status.
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.menu() });
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.status() });
      queryClient.invalidateQueries({ queryKey: queryKeys.campaignQuestions.all });
    },
  });
}

/**
 * Record an onboarding step transition (SelfIdCompleted, InDemo, Completed, Skipped).
 */
export function useSetOnboardingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: OnboardingStatus) => api.onboarding.setStatus({ status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.status() });
    },
  });
}

/**
 * Request hands-on onboarding help (e.g. voter-file retrieval). Captures the request for the
 * team to follow up — no campaign-data upload.
 */
export function useRequestAssistance() {
  return useMutation({
    mutationFn: (body: AssistanceRequestBody) => api.onboarding.requestAssistance(body),
  });
}
